import React, {
  ComponentPropsWithoutRef,
  ElementType,
  forwardRef,
  ReactElement,
  ReactNode,
  Ref
} from 'react'

import { bemJoin, classNames } from '@app/lib/bem'

import './InputBase.scss'

export type InputBaseSize = 'xs' | 'sm' | 'md' | 'lg'

type InputBaseOwnProps = {
  size?: InputBaseSize
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  error?: boolean
  disabled?: boolean
  children?: ReactNode
  className?: string
}

export type InputBaseProps<T extends ElementType = 'div'> = InputBaseOwnProps & {
  as?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof InputBaseOwnProps | 'as'>

const b = bemJoin('input-base')

const InputBaseInner = <T extends ElementType = 'div'>(
  {
    as,
    size = 'md',
    leadingIcon,
    trailingIcon,
    error,
    disabled,
    className,
    children,
    ...rest
  }: InputBaseProps<T>,
  ref: Ref<Element>
) => {
  const Root = (as || 'div') as ElementType

  return (
    <Root
      ref={ref}
      disabled={disabled}
      className={classNames(b({ [size]: true, error: !!error, disabled: !!disabled }), className)}
      {...rest}
    >
      {leadingIcon && <span className={b('icon', { leading: true })}>{leadingIcon}</span>}
      <span className={b('content')}>{children}</span>
      {trailingIcon && <span className={b('icon', { trailing: true })}>{trailingIcon}</span>}
    </Root>
  )
}

export const InputBase = forwardRef(InputBaseInner) as <T extends ElementType = 'div'>(
  props: InputBaseProps<T> & { ref?: Ref<Element> }
) => ReactElement | null
